import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AutopilotDetail,
  AutopilotAutomatic,
  AgentEvents,
  ThreadActivityUpdateEvent,
} from '@dreamai/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import api from '../../../api';
import { AppTab } from './appLayoutConfig';
import { useAgent } from '../../../contexts/AgentContext';
import { usePusher } from '../../../contexts/PusherContext';
import ProjectAppResearchSection from './ProjectAppResearchSection';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import './autopilot-wrapper.scss';

interface AutopilotWrapperProps {
  autopilotId: string;
  threadId?: string;
  tabId?: string;
  onTabsReady?: (tabs: AppTab[]) => void;
}

type AutopilotData = Pick<
  AutopilotDetail,
  'id' | 'name' | 'instructions' | 'notes' | 'automatics'
>;

/**
 * Convert a 6-field cron expression to a human-readable schedule description.
 * Format: second minute hour day-of-month month day-of-week
 */
function describeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 6) return cron;

  const [, minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  const dayNames: Record<string, string> = {
    '0': 'Sunday',
    '1': 'Monday',
    '2': 'Tuesday',
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    '7': 'Sunday',
  };

  const formatTime = (h: string, m: string) => {
    const hr = parseInt(h, 10);
    const min = parseInt(m, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const displayHr = hr % 12 || 12;
    return `${displayHr}:${String(min).padStart(2, '0')} ${ampm}`;
  };

  // Every weekday at a specific time
  if (
    dayOfWeek === '1-5' &&
    dayOfMonth === '*' &&
    month === '*' &&
    hour !== '*' &&
    minute !== '*'
  ) {
    return `Weekdays at ${formatTime(hour, minute)}`;
  }

  // Specific days of week
  if (
    dayOfWeek !== '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    hour !== '*'
  ) {
    const days = dayOfWeek
      .split(',')
      .map((d) => dayNames[d] || d)
      .join(', ');
    return `${days} at ${formatTime(hour, minute)}`;
  }

  // Daily at a specific time
  if (
    dayOfWeek === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    hour !== '*' &&
    minute !== '*'
  ) {
    return `Daily at ${formatTime(hour, minute)}`;
  }

  // Every N hours
  if (hour.startsWith('*/')) {
    const interval = hour.slice(2);
    return `Every ${interval} hour${interval === '1' ? '' : 's'}`;
  }

  // Every N minutes
  if (minute.startsWith('*/') && hour === '*') {
    const interval = minute.slice(2);
    return `Every ${interval} minute${interval === '1' ? '' : 's'}`;
  }

  return cron;
}

function describeAutomatic(auto: AutopilotAutomatic): string {
  if (auto.type === 'RECURRING' && auto.frequency) {
    return describeCron(auto.frequency);
  }
  if (auto.type === 'TRIGGERED' && auto.trigger_category === 'EMAIL_RECEIVED') {
    return auto.trigger_details
      ? `When email received: ${auto.trigger_details}`
      : 'When an email is received';
  }
  return auto.description || 'Custom trigger';
}

const AUTOPILOT_TABS: AppTab[] = [{ id: 'overview', label: 'Overview' }];

const AutopilotWrapper: React.FC<AutopilotWrapperProps> = ({
  autopilotId,
  threadId,
  tabId = 'overview',
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { selectedAgent, agents } = useAgent();
  const pusherChannel = usePusher();
  const agentName =
    selectedAgent?.label ||
    agents[0]?.nickname ||
    agents[0]?.name ||
    'Your assistant';
  const [autopilotData, setAutopilotData] = useState<AutopilotData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTabsRef = useRef<string>('');

  // Edit state for instructions
  const [isEditing, setIsEditing] = useState(false);
  const [editedInstructions, setEditedInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const fetchAutopilot = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/autopilots/${autopilotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setAutopilotData({
          id: response.data.autopilot.id,
          name: response.data.autopilot.name,
          instructions: response.data.autopilot.instructions,
          notes: response.data.autopilot.notes,
          automatics: response.data.autopilot.automatics,
        });
      } else {
        setError('Failed to load autopilot');
      }
    } catch (err) {
      console.error('Error fetching autopilot:', err);
      setError('Failed to load autopilot');
    } finally {
      setLoading(false);
    }
  }, [autopilotId, getAccessTokenSilently]);

  // Reset state when autopilotId changes
  useEffect(() => {
    setLoading(true);
    setAutopilotData(null);
    setError(null);
    setIsEditing(false);
    lastTabsRef.current = '';
    fetchAutopilot();
  }, [autopilotId, fetchAutopilot]);

  // Notify parent of tabs
  useEffect(() => {
    if (loading || !onTabsReady) return;

    const tabs = AUTOPILOT_TABS;
    const tabsKey = JSON.stringify(tabs);

    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [loading, onTabsReady]);

  // Re-fetch when thread gets activity (agent updates notes/instructions/automatics)
  useEffect(() => {
    if (!threadId) return;
    const handler = (event: ThreadActivityUpdateEvent) => {
      if (event.thread_id === threadId && !isEditing) {
        fetchAutopilot();
      }
    };
    pusherChannel.bind(AgentEvents.THREAD_ACTIVITY_UPDATE, handler);
    return () => {
      pusherChannel.unbind(AgentEvents.THREAD_ACTIVITY_UPDATE, handler);
    };
  }, [pusherChannel, threadId, fetchAutopilot, isEditing]);

  // Re-fetch on window focus
  useEffect(() => {
    const handleFocus = () => {
      if (!isEditing) fetchAutopilot();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchAutopilot, isEditing]);

  // Auto-resize textarea
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const ta = textareaRef.current;
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
      ta.focus();
    }
  }, [isEditing, editedInstructions]);

  const handleEditClick = () => {
    if (autopilotData) {
      setEditedInstructions(autopilotData.instructions);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedInstructions('');
  };

  const handleSave = async () => {
    if (!autopilotData) return;

    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch(
        `/autopilots/${autopilotId}/instructions`,
        { instructions: editedInstructions },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setAutopilotData({
          ...autopilotData,
          instructions: editedInstructions,
        });
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error saving instructions:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className='autopilot-wrapper loading'></div>;
  }

  if (error || !autopilotData) {
    return null;
  }

  const automatics = autopilotData.automatics || [];
  const activeAutomatics = automatics.filter((a) => a.active);

  return (
    <div className='autopilot-wrapper'>
      <div className='autopilot-overview'>
        {/* Schedule Section */}
        <div className='autopilot-card schedule-card'>
          <div className='card-header'>
            <h3>When will this run</h3>
          </div>
          <div className='card-body'>
            {activeAutomatics.length === 0 ? (
              <p className='empty-state'>
                No schedule configured yet. {agentName} will set one up based on
                your instructions.
              </p>
            ) : (
              <div className='schedule-list'>
                {activeAutomatics.map((auto) => (
                  <div key={auto.id} className='schedule-item'>
                    <div className='schedule-icon'>
                      {auto.type === 'RECURRING' ? '🔄' : '📧'}
                    </div>
                    <div className='schedule-detail'>
                      <span className='schedule-label'>
                        {describeAutomatic(auto)}
                      </span>
                      {auto.type === 'RECURRING' && auto.next_run_at && (
                        <span className='schedule-next'>
                          Next:{' '}
                          {new Date(auto.next_run_at).toLocaleString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Instructions Section */}
        <div className='autopilot-card instructions-card'>
          <div className='card-header'>
            <h3>Instructions</h3>
            {!isEditing && (
              <button
                className='edit-button'
                onClick={handleEditClick}
                title='Edit instructions'
              >
                <EditIcon />
              </button>
            )}
          </div>
          <div className='card-body'>
            {isEditing ? (
              <div className='edit-area'>
                <textarea
                  ref={textareaRef}
                  value={editedInstructions}
                  onChange={(e) => setEditedInstructions(e.target.value)}
                  className='instructions-textarea editing'
                />
                <div className='edit-actions'>
                  <button
                    className='cancel-button'
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className='save-button'
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <article className='instructions-display md-content'>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {autopilotData.instructions || 'No instructions yet.'}
                </ReactMarkdown>
              </article>
            )}
          </div>
        </div>

        {/* Documents Section */}
        {threadId && (
          <ProjectAppResearchSection
            threadId={threadId}
            threadName={autopilotData.name}
            headingLabel='Documents'
          />
        )}

        {/* Notes Section */}
        {autopilotData.notes && (
          <div className='autopilot-card notes-card'>
            <div className='card-header'>
              <h3>Notes</h3>
            </div>
            <div className='card-body'>
              <article className='notes-display md-content'>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {autopilotData.notes}
                </ReactMarkdown>
              </article>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutopilotWrapper;
